import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import CustomDropdown from '../CustomDropdown';
import Pagination from '../Pagination';
import ProgressBar from '../ProgressBar';
import { IoSearch } from 'react-icons/io5';
import ExportButton from '../../components/ExportButton';
import Button from '../Button';
import SellerBatteryService from '../../api/service/seller/SellerBatteryService';
import { BatteryData } from '../../api/service/BatteryInterfaces';
import generateDiagnosisTab1PDF from './DiagnosisTab1PDF';
import CustomSelect from '../../components/CustomSelect';
import { useNavigate } from 'react-router-dom';
import { DiagnosisPageUrl } from '../../url/poenUrl';
import LoadingOverlay from '../../components/LoadingOverlay';
import { SaleStatus } from '../../api/service/UtilsInterface';

const Container = styled.div``;

const Header = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 20px;
  @media (max-width: 768px) {
    flex-direction: column;
  }
`;

const SearchBar = styled.div`
  display: flex;
  gap: 10px;
  align-items: center;
  flex-wrap: wrap;
  justify-content: space-evenly;
  label {
    font-size: 16px;
  }

  @media (max-width: 768px) {
    gap: 5px;
    label {
      font-size: 12px;
    }
  }
  @media (max-width: 350px) {
    label {
      font-size: 10px;
    }
  }
`;

const SearchBarInput = styled.input`
  padding: 5px 10px;
  border: 1px solid #ddd;
  border-radius: 5px;
  @media (max-width: 768px) {
    width: 100px;
  }
`;

const IoSearchIcon = styled.div`
  width: 30px;
  height: 30px;
  font-size: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 50%;
  color: ${(props) => props.theme.colors.whiteColor};
  background-color: ${(props) => props.theme.colors.mainColor};

  @media (max-width: 768px) {
    width: 20px;
    height: 20px;
    font-size: 14px;
  }
`;

const DropdownGroup = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  align-items: center;
  justify-content: space-evenly;
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 10px;
`;

const TableContainer = styled.div`
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: ${(props) => props.theme.colors.whiteColor};
`;

const TableBox = styled.div`
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: ${(props) => props.theme.colors.whiteColor};
  overflow-x: auto;

  @media (max-width: 768px) {
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;
  }
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-top-left-radius: 10px;
  border-top-right-radius: 10px;
  background-color: white;

  tr {
    border-bottom: 1px solid #ddd;
  }

  th,
  td {
    font-size: 16px;
    padding: 10px;
    text-align: center;
    vertical-align: middle; /* 세로 정렬 */
    white-space: nowrap; /* 줄바꿈 방지 */
  }

  th {
    background-color: #85c1a9;
    color: white;
    font-weight: 500;
  }

  th:first-child {
    border-top-left-radius: 10px;
  }

  th:last-child {
    border-top-right-radius: 10px;
  }

  @media (max-width: 768px) {
    border-top-left-radius: 0px;
    border-top-right-radius: 0px;

    tr {
      border-bottom: 1px solid #ddd;
    }

    th,
    td {
      font-size: 12px;
      padding: 10px;
      text-align: center;
      vertical-align: middle; /* 세로 정렬 */
      white-space: nowrap; /* 줄바꿈 방지 */
    }

    th {
      font-weight: 400;
    }

    th:first-child {
      border-top-left-radius: 0px;
    }

    th:last-child {
      border-top-right-radius: 0px;
    }
  }
`;

function DiagnosisTab1() {
  const navigate = useNavigate(); // navigate 훅 추가
  const [isPDFLoading, setIsPDFLoading] = useState(false); // 로딩 상태 추가

  const [currentPage, setCurrentPage] = useState<number>(0);
  const [totalPages, setTotalPages] = useState<number>(1); // 총 페이지 수
  const [tableData, setTableData] = useState<BatteryData[]>([]); // BatteryData 타입 사용

  const [identifier, setIdentifier] = useState<string>(''); // 고유번호 입력 값
  const [selectedSaleStatus, setSelectedSaleStatus] = useState<SaleStatus | ''>(
    ''
  );
  const [manufacturerOptions, setManufacturerOptions] = useState<
    { key: string; value: string; label: string }[]
  >([]); // 제조사 옵션 상태
  const [selectedManufacturer, setSelectedManufacturer] = useState<string>(''); // 제조사

  const [modelOptions, setModelOptions] = useState<
    { key: string; value: string; label: string }[]
  >([]); // 차종 옵션
  const [selectedModel, setSelectedModel] = useState<string>(''); // 선택된 차종

  const [selectedSortOrder, setSelectedSortOrder] = useState<string>(''); // 정렬 기준 상태

  const [selectedPriceRange, setSelectedPriceRange] = useState<string>(''); // 선택된 판매 금액 범위

  const [selectedRows, setSelectedRows] = useState<Set<string>>(new Set());
  const [selectAll, setSelectAll] = useState<boolean>(false);

  const saleStatusOptions = [
    { label: '매물 상태', value: '' },
    { label: '미등록', value: SaleStatus.NOT_REGISTERED },
    { label: '판매 중', value: SaleStatus.ON_SALE },
    { label: '판매 예약', value: SaleStatus.RESERVED },
    { label: '판매 완료', value: SaleStatus.FINISHED },
  ];

  // 판매 금액 옵션
  const priceRangeOptions = [
    { key: '', value: '', label: '판매금액' }, // 전체 옵션 추가
    { key: 'below500', value: 'below500', label: '500만원 미만' },
    { key: '500to1000', value: '500to1000', label: '500~1,000만원' },
    { key: 'above1000', value: 'above1000', label: '1,000만원 초과' },
  ];

  // 검색 버튼 클릭 시
  const handleSearch = () => {
    if (identifier) {
      fetchBatteryList(); // identifier 기반으로 검색
    } else {
      fetchBatteryList(currentPage); // 다른 조건들 기반으로 검색
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    if (/^[a-zA-Z0-9]*$/.test(value)) {
      // 숫자, 대문자 및 소문자 영어만 허용
      setIdentifier(value);
    }
  };

  const handlePageChange = (page: number) => {
    //console.log('페이지 변경:', page + 1);
    setCurrentPage(page);
    fetchBatteryList(page); // 페이지 변경 시 데이터 로드
  };

  // 배터리 리스트 API 호출
  const fetchBatteryList = async (page: number = 0) => {
    try {
      let minPrice, maxPrice;

      // 선택된 가격 범위에 따라 minPrice와 maxPrice 설정
      switch (selectedPriceRange) {
        case 'below500':
          minPrice = 0;
          maxPrice = 500;
          break;
        case '500to1000':
          minPrice = 500;
          maxPrice = 1000;
          break;
        case 'above1000':
          minPrice = 1001; // 1,000만원 초과
          maxPrice = undefined; // 상한 없음
          break;
        default:
          minPrice = undefined;
          maxPrice = undefined;
          break;
      }

      const params = {
        page: page,
        size: 10,
        identifier: identifier || undefined, // 입력된 고유번호 값
        saleStatus: selectedSaleStatus || undefined, // 선택된 매물 상태
        carManufacturerId: selectedManufacturer || undefined,
        carModelId: selectedModel || undefined,
        sortBy: selectedSortOrder as 'asc' | 'desc' | undefined, // 올바른 타입으로 변환
        minPrice, // 최소 가격
        maxPrice, // 최대 가격
      };

      const response = await SellerBatteryService.getBatteryList(params); // API 호출
      setTableData(response.data.content || []); // 데이터 저장
      setTotalPages(response.data.totalPages); // 총 페이지 수 저장
    } catch (error) {
      console.error('배터리 리스트 조회 중 오류:', error);
    }
  };

  const fetchAllBatteryList = async (): Promise<BatteryData[]> => {
    try {
      let allData: BatteryData[] = []; // 타입 명시
      let page = 0;
      let totalPages = 1;

      let minPrice, maxPrice;

      // 선택된 가격 범위에 따라 minPrice와 maxPrice 설정
      switch (selectedPriceRange) {
        case 'below500':
          minPrice = 0;
          maxPrice = 500;
          break;
        case '500to1000':
          minPrice = 500;
          maxPrice = 1000;
          break;
        case 'above1000':
          minPrice = 1001; // 1,000만원 초과
          maxPrice = undefined; // 상한 없음
          break;
        default:
          minPrice = undefined;
          maxPrice = undefined;
          break;
      }

      while (page < totalPages) {
        const response = await SellerBatteryService.getBatteryList({
          page,
          size: 1000,
          identifier: identifier || undefined,
          saleStatus: selectedSaleStatus || undefined,
          carManufacturerId: selectedManufacturer || undefined,
          carModelId: selectedModel || undefined,
          sortBy: selectedSortOrder as 'asc' | 'desc' | undefined, // 타입 캐스팅 추가
          minPrice, // 최소 가격
          maxPrice, // 최대 가격
        });

        allData = [...allData, ...response.data.content];
        totalPages = response.data.totalPages;
        page++;
      }

      return allData;
    } catch (error) {
      console.error('전체 데이터 조회 중 오류:', error);
      return [];
    }
  };

  // 제조사 목록을 가져오는 함수
  const fetchManufacturers = async () => {
    try {
      const response = await SellerBatteryService.getBatteryManufacturers(); // 제조사 목록 API 호출
      const manufacturers = response.data.map((manufacturer) => ({
        key: manufacturer.carManufacturerId, // 고유 ID
        value: manufacturer.carManufacturerId, // 값
        label: manufacturer.carManufacturerName, // 표시 이름
      }));

      // 기본 옵션과 API 데이터를 합치기
      setManufacturerOptions([
        { key: '', value: '', label: '제조사' }, // 기본 옵션
        ...manufacturers, // API에서 가져온 옵션
      ]);
    } catch (error) {
      console.error('제조사 목록 조회 중 오류:', error);
    }
  };

  // 선택된 제조사에 따른 차종 목록 가져오기
  const fetchModels = async (manufacturerId: string) => {
    try {
      if (!manufacturerId) {
        setModelOptions([]); // 제조사가 선택되지 않으면 차종 옵션 초기화
        return;
      }

      const response =
        await SellerBatteryService.getManufacturersModel(manufacturerId);
      const models = response.data.map((model) => ({
        key: model.carModelId,
        value: model.carModelId,
        label: model.carModelName,
      }));
      setModelOptions([
        { key: '', value: '', label: '차종' }, // 기본 옵션
        ...models,
      ]);
    } catch (error) {
      console.error('차종 목록 조회 중 오류:', error);
    }
  };

  useEffect(() => {
    sessionStorage.removeItem('DiagnosisbatteryId');
    fetchManufacturers(); // 제조사 목록 불러오기
    fetchBatteryList(currentPage);
  }, []);

  useEffect(() => {
    // tableData가 업데이트되면 첫 번째 항목의 batteryId를 sessionStorage에 저장
    if (tableData.length > 0) {
      const firstBatteryId = tableData[0].batteryId;

      // 만약 sessionStorage에 이미 값이 저장되어 있다면 덮어쓰지 않음
      const existingBatteryId = sessionStorage.getItem('DiagnosisbatteryId');
      if (!existingBatteryId) {
        sessionStorage.setItem('DiagnosisbatteryId', firstBatteryId);
      }
    }
  }, [tableData]);

  useEffect(() => {
    // 제조사가 변경될 때 차종 옵션 초기화 및 데이터 다시 불러오기
    setSelectedModel(''); // 차종 선택 초기화
    fetchModels(selectedManufacturer); // 선택된 제조사에 따라 차종 목록 업데이트
  }, [selectedManufacturer]);

  const handleSaleStatusChange = async (
    batteryId: string,
    newStatus: string
  ) => {
    try {
      // 서버에 변경 요청
      await SellerBatteryService.updateBatterySaleStatus(batteryId, newStatus);
      //console.log(`매물 상태가 ${newStatus}로 변경되었습니다.`);
      // 변경된 데이터로 테이블 업데이트
      fetchBatteryList(currentPage);
    } catch (error) {
      alert(error);
      console.error('매물 상태 변경 중 오류:', error);
    }
  };

  const handleDownloadPDF = async () => {
    try {
      setIsPDFLoading(true); // 로딩 시작
      const allTableData = await fetchAllBatteryList();
      const pdfBytes = await generateDiagnosisTab1PDF(allTableData); // 테이블 데이터를 PDF로 변환
      const blob = new Blob([pdfBytes], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = '진단정보_진단리스트.pdf';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('PDF 생성 중 오류 발생:', error);
    } finally {
      setIsPDFLoading(false); // 로딩 종료
    }
  };

  // handleSelectAll: thead의 체크박스를 클릭하면 모든 체크박스를 선택/해제
  const handleSelectAll = () => {
    if (selectAll) {
      setSelectedRows(new Set()); // 모든 체크박스를 해제
    } else {
      const allRows = tableData.map((item) => item.batteryId);
      setSelectedRows(new Set(allRows)); // 모든 체크박스를 선택
    }
    setSelectAll(!selectAll); // selectAll 상태 반전
  };

  // handleRowSelect: tbody의 체크박스를 클릭하면 해당 체크박스의 상태를 변경
  const handleRowSelect = (batteryId: string) => {
    const newSelectedRows = new Set(selectedRows);
    if (newSelectedRows.has(batteryId)) {
      newSelectedRows.delete(batteryId); // 이미 선택되어 있으면 해제
    } else {
      newSelectedRows.add(batteryId); // 선택
    }
    setSelectedRows(newSelectedRows);
  };

  // handleSelectAllChange: tbody에서 체크박스가 모두 선택되면 thead의 체크박스를 자동으로 체크
  useEffect(() => {
    const allChecked = tableData.every((item) =>
      selectedRows.has(item.batteryId)
    );
    setSelectAll(allChecked); // 모든 체크박스가 선택되었으면 selectAll을 true로 설정
  }, [selectedRows, tableData]);

  const handleRowClick = (batteryId: string) => {
    sessionStorage.setItem('DiagnosisbatteryId', batteryId);
    navigate(`${DiagnosisPageUrl}?activeTab=1`); // DiagnosisTab2로 이동
  };

  const handleDelete = async () => {
    if (selectedRows.size === 0) {
      alert('삭제할 배터리를 선택해주세요.');
      return;
    }

    const confirmDelete = window.confirm('선택한 배터리를 삭제하시겠습니까?');
    if (!confirmDelete) return;

    try {
      const batteryIds = Array.from(selectedRows); // 선택된 배터리 ID를 배열로 변환

      // 선택된 배터리 ID를 한 번에 삭제
      await SellerBatteryService.deleteBattery({ batteryIds });

      alert('삭제가 완료되었습니다.');
      await fetchBatteryList(0); // 삭제 후 데이터 갱신
      setSelectedRows(new Set()); // 선택된 행 초기화
    } catch (error) {
      console.error('삭제 중 오류 발생:', error);
      alert(error);
    }
  };

  return (
    <Container>
      {isPDFLoading && <LoadingOverlay />}
      <Header>
        <SearchBar>
          <label htmlFor="identifier">고유번호 조회</label>
          <SearchBarInput
            type="text"
            value={identifier}
            onChange={handleInputChange}
            placeholder="고유번호를 입력해 주세요"
          />
          <IoSearchIcon onClick={handleSearch}>
            <IoSearch />
          </IoSearchIcon>
          <ExportButton pc={false} onClick={handleDownloadPDF} />
        </SearchBar>
        <DropdownGroup>
          <CustomDropdown
            options={saleStatusOptions.map((option) => ({
              key: option.value,
              value: option.value,
              label: option.label,
            }))}
            selected={selectedSaleStatus}
            onSelect={(value: string) =>
              setSelectedSaleStatus(value as SaleStatus)
            }
            placeholder="매물 상태"
          />
          <CustomDropdown
            options={manufacturerOptions}
            selected={selectedManufacturer}
            onSelect={setSelectedManufacturer}
            placeholder={'제조사'}
          />
          <CustomDropdown
            options={modelOptions}
            selected={selectedModel}
            onSelect={setSelectedModel}
            placeholder={'차종'}
          />
          <CustomDropdown
            options={[
              { key: 'desc', value: 'desc', label: '최신순' },
              { key: 'asc', value: 'asc', label: '오래된순' },
            ]}
            selected={selectedSortOrder} // 새로운 상태로 선택된 옵션 관리
            onSelect={setSelectedSortOrder} // 상태 업데이트
            placeholder={'측정일'}
          />
          <CustomDropdown
            options={priceRangeOptions}
            selected={selectedPriceRange}
            onSelect={setSelectedPriceRange} // 선택된 가격 범위 업데이트
            placeholder={'판매금액'}
          />
          <ButtonContainer>
            <Button save={true} onClick={handleSearch}>
              검색
            </Button>
            {localStorage.getItem('permissions') === 'SELLER' && (
              <Button save={false} onClick={handleDelete}>
                삭제
              </Button>
            )}
          </ButtonContainer>
        </DropdownGroup>
        <ExportButton pc={true} onClick={handleDownloadPDF} />
      </Header>
      {/* <DiagnosisTab1HiddenPDFContainer ref={pdfRef} tableData={tableData} /> */}
      <TableContainer id="table-container">
        <TableBox>
          <Table>
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    checked={selectAll}
                    onChange={handleSelectAll}
                  />
                </th>
                <th>NO</th>
                <th>매물상태</th>
                <th>고유번호</th>
                <th>회사명</th>
                <th>제조사</th>
                <th>차량</th>
                <th>SOH</th>
                <th>SOC</th>
                <th>셀편차</th>
                <th>고장코드</th>
                <th>측정일</th>
                <th>판매금액</th>
              </tr>
            </thead>
            <tbody>
              {tableData.length === 0 && (
                <tr>
                  <td
                    colSpan={13}
                    style={{ textAlign: 'center', padding: '20px' }}
                  >
                    데이터가 없습니다.
                  </td>
                </tr>
              )}
              {tableData.map((item, index) => (
                <tr
                  key={item.batteryId}
                  onClick={() => handleRowClick(item.batteryId)}
                >
                  <td
                    onClick={(e) => e.stopPropagation()} // <td> 레벨에서 이벤트 차단
                  >
                    <input
                      type="checkbox"
                      checked={selectedRows.has(item.batteryId)}
                      onChange={() => handleRowSelect(item.batteryId)}
                    />
                  </td>
                  <td>{index + 1}</td>
                  <td
                    onClick={(e) => e.stopPropagation()} // <td> 레벨에서 이벤트 차단
                  >
                    {localStorage.getItem('permissions') === 'SELLER' ? (
                      <CustomSelect
                        value={item.saleStatus}
                        onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                          e.stopPropagation(); // 이벤트 전파 방지

                          const selectedOption = [
                            { value: 'NOT_REGISTERED', label: '미등록' },
                            {
                              value: 'ON_SALE',
                              label: '판매중',
                              disabled: !item.price,
                            },
                            {
                              value: 'RESERVED',
                              label: '판매예약',
                              disabled: !item.price,
                            },
                            {
                              value: 'FINISHED',
                              label: '판매완료',
                              disabled: !item.price,
                            },
                          ].find((option) => option.value === e.target.value);

                          if (selectedOption?.disabled) {
                            alert(
                              '판매금액이 입력되지 않은 상태에서는 변경할 수 없습니다.'
                            );
                            return; // 경고창 띄운 뒤 상태 변경 방지
                          }

                          const updatedStatus = e.target.value;
                          //console.log(`매물 상태 변경: ${updatedStatus}`);
                          handleSaleStatusChange(item.batteryId, updatedStatus);
                        }}
                        options={[
                          { value: 'NOT_REGISTERED', label: '미등록' },
                          {
                            value: 'ON_SALE',
                            label: '판매중',
                          },
                          {
                            value: 'RESERVED',
                            label: '판매예약',
                          },
                          {
                            value: 'FINISHED',
                            label: '판매완료',
                          },
                        ]}
                      />
                    ) : (
                      // SELLER가 아닐 경우 텍스트로 매물 상태 표시
                      <span
                        style={{
                          color:
                            item.saleStatus === '판매예약'
                              ? '#0083D4'
                              : item.saleStatus === '판매완료'
                                ? '#D40000'
                                : '#000',
                        }}
                      >
                        {item.saleStatus}
                      </span>
                    )}
                  </td>
                  <td>{item.identifier}</td>
                  <td>{item.company}</td>
                  <td>{item.manufacturer}</td>
                  <td>{item.model}</td>
                  {/* SOH와 SOC에서 ProgressBar 컴포넌트 사용 */}
                  <td>
                    <ProgressBar percentage={item.soh} type="SOH" />
                  </td>
                  <td>
                    <ProgressBar percentage={item.soc} type="SOC" />
                  </td>
                  <td>{item.cellDeviation}</td>
                  <td>{item.failureCodeYN}</td>
                  <td>{item.measurementDate}</td>
                  <td>{item.price ? `${item.price.toLocaleString()}` : '-'}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        </TableBox>
        <Pagination
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={handlePageChange}
        />
      </TableContainer>
    </Container>
  );
}

export default DiagnosisTab1;
